import React from 'react';
import { Helmet } from 'react-helmet-async';

const RefundPolicy = () => {
  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px'
  };

  const pageWrapperStyle = {
    paddingTop: '80px',
    paddingBottom: '80px'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '60px',
    paddingBottom: '40px',
    borderBottom: '3px solid #52A7B0',
    animation: 'fadeIn 0.8s ease-in'
  };

  const headerTitleStyle = {
    fontSize: '2.8rem',
    color: '#1a4a5a',
    marginBottom: '10px',
    fontWeight: '700',
    margin: '0 0 15px 0',
    letterSpacing: '-0.5px'
  };

  const headerSubtitleStyle = {
    fontSize: '1.05rem',
    color: '#306873',
    marginTop: '15px',
    fontWeight: '400'
  };

  const contentStyle = {
    backgroundColor: '#fafafa',
    padding: '50px 50px',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e8f0f2'
  };

  const sectionStyle = {
    marginBottom: '45px',
    paddingBottom: '35px',
    borderBottom: '1px solid #e5e5e5'
  };

  const lastSectionStyle = {
    ...sectionStyle,
    borderBottom: 'none',
    marginBottom: '0',
    paddingBottom: '0'
  };

  const h2Style = {
    fontSize: '1.6rem',
    color: '#1a4a5a',
    marginBottom: '20px',
    marginTop: '0',
    fontWeight: '700',
    lineHeight: '1.4',
    paddingBottom: '10px',
    borderBottom: '2px solid #52A7B0'
  };

  const h3Style = {
    fontSize: '1.25rem',
    color: '#306873',
    marginTop: '28px',
    marginBottom: '18px',
    fontWeight: '700',
    letterSpacing: '0.3px'
  };

  const pStyle = {
    fontSize: '0.95rem',
    color: '#444',
    marginBottom: '18px',
    lineHeight: '1.9'
  };

  const ulStyle = {
    margin: '20px 0',
    paddingLeft: '28px',
    listStyle: 'none'
  };

  const liStyle = {
    marginBottom: '16px',
    paddingLeft: '24px',
    position: 'relative',
    color: '#444',
    fontSize: '0.95rem',
    lineHeight: '1.8',
    transition: 'transform 0.2s ease'
  };

  const strongStyle = {
    color: '#1a4a5a',
    fontWeight: '600'
  };

  const contactBoxStyle = {
    backgroundImage: 'linear-gradient(135deg, #e8f4f6 0%, #f0f9fb 100%)',
    padding: '30px',
    borderLeft: '6px solid #52A7B0',
    borderRadius: '8px',
    margin: '30px 0',
    boxShadow: '0 2px 8px rgba(82, 167, 176, 0.1)'
  };

  const linkStyle = {
    color: '#52A7B0',
    textDecoration: 'none',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    borderBottom: '2px solid transparent'
  };

  const PolicySection = ({ title, children }) => (
    <section style={sectionStyle}>
      <h2 style={h2Style}>{title}</h2>
      {children}
    </section>
  );

  const LastPolicySection = ({ title, children }) => (
    <section style={lastSectionStyle}>
      <h2 style={h2Style}>{title}</h2>
      {children}
    </section>
  );

  const ListItem = ({ children }) => (
    <li style={liStyle}>
      <span style={{position: 'absolute', left: '0', color: '#52A7B0', fontWeight: 'bold'}}>→</span>
      {children}
    </li>
  );

  return (
    <>
      <Helmet>
        <title>Refund Policy - WorkdeskVA Remote Staffing Solutions</title>
        <meta name="description" content="Refund Policy for WorkdeskVA remote staffing services. Understand our cancellation, refund terms, and service guarantees." />
      </Helmet>
      <div style={pageWrapperStyle}>
        <div style={containerStyle}>
          <div style={headerStyle}>
            <h1 style={headerTitleStyle}>Refund Policy</h1>
            <p style={headerSubtitleStyle}>
              Transparent refund and cancellation terms for WorkdeskVA remote staffing services.
            </p>
          </div>

          <div style={contentStyle}>
            <PolicySection title="1. Overview">
              <p style={{...pStyle, fontSize: '1.05rem', color: '#333', fontWeight: '500'}}>
                At <span style={strongStyle}>WorkdeskVA</span>, we are committed to your satisfaction with our remote staffing services. This refund policy outlines our terms for cancellations, refunds, and service guarantees.
              </p>
              <p style={pStyle}>
                We believe in fair and transparent business practices. If you're not completely satisfied with our services, we offer refund options based on the circumstances outlined in this policy.
              </p>
            </PolicySection>

            <PolicySection title="2. Refund Eligibility">
              <p style={pStyle}>
                We offer refunds under the following conditions:
              </p>
              <h3 style={h3Style}>Eligible for Refund</h3>
              <ul style={ulStyle}>
                <ListItem>Cancellation within <span style={strongStyle}>7 days</span> of service start with no work delivered</ListItem>
                <ListItem>Payment made in error or duplicate payment</ListItem>
                <ListItem>Service not provided as described in the agreement</ListItem>
                <ListItem>Technical issues preventing access to services for more than 48 hours</ListItem>
                <ListItem>Mutual agreement between WorkdeskVA and the client</ListItem>
              </ul>

              <h3 style={h3Style}>Not Eligible for Refund</h3>
              <ul style={ulStyle}>
                <ListItem>Cancellation after 7 days of service start (prorated options available)</ListItem>
                <ListItem>Services already delivered or work in progress</ListItem>
                <ListItem>Client-initiated cancellations after team onboarding is complete</ListItem>
                <ListItem>Disputes over service quality without documented evidence</ListItem>
                <ListItem>Non-payment or account suspension due to policy violation</ListItem>
              </ul>
            </PolicySection>

            <PolicySection title="3. Refund Timeline">
              <p style={pStyle}>
                We process refunds according to the following timeline:
              </p>
              <h3 style={h3Style}>Processing Timeline</h3>
              <ul style={ulStyle}>
                <ListItem><span style={strongStyle}>Refund Request:</span> Must be submitted within 30 days of payment</ListItem>
                <ListItem><span style={strongStyle}>Review Period:</span> 5-7 business days to review and approve request</ListItem>
                <ListItem><span style={strongStyle}>Processing:</span> 3-5 business days after approval</ListItem>
                <ListItem><span style={strongStyle}>Bank Transfer:</span> Additional 3-7 business days depending on your bank</ListItem>
                <ListItem><span style={strongStyle}>Credit Card:</span> Refund appears as credit within 1-2 billing cycles</ListItem>
              </ul>
            </PolicySection>

            <PolicySection title="4. Cancellation Policy">
              <p style={pStyle}>
                Our cancellation policy provides flexibility for our clients:
              </p>
              <h3 style={h3Style}>Immediate Cancellation (Full Refund)</h3>
              <ul style={ulStyle}>
                <ListItem>Valid only within 7 days of service initiation</ListItem>
                <ListItem>No work must have been delivered or started</ListItem>
                <ListItem>100% refund of service charges</ListItem>
              </ul>

              <h3 style={h3Style}>Within 30 Days (Prorated Refund)</h3>
              <ul style={ulStyle}>
                <ListItem>Refund based on unused service period</ListItem>
                <ListItem>Calculated on daily rate: (Days Remaining / Total Days) × Total Amount</ListItem>
                <ListItem>Administrative fee of 5% may apply</ListItem>
              </ul>

              <h3 style={h3Style}>After 30 Days</h3>
              <ul style={ulStyle}>
                <ListItem>Monthly billing cycle completes</ListItem>
                <ListItem>Service continues through end of current billing period</ListItem>
                <ListItem>Can cancel effective the first day of next billing cycle</ListItem>
                <ListItem>No refund for services already rendered</ListItem>
              </ul>
            </PolicySection>

            <PolicySection title="5. Service Replacement Guarantee">
              <p style={pStyle}>
                We stand behind our team members and offer replacement guarantees:
              </p>
              <h3 style={h3Style}>Team Member Replacement</h3>
              <ul style={ulStyle}>
                <ListItem>If you're unsatisfied with a team member, we offer one free replacement within 14 days</ListItem>
                <ListItem>Replacement costs nothing - only a brief transition period</ListItem>
                <ListItem>We'll work to match you with a better-fit candidate</ListItem>
                <ListItem>Additional replacements available at reduced rates</ListItem>
              </ul>

              <h3 style={h3Style}>Service Quality Guarantee</h3>
              <ul style={ulStyle}>
                <ListItem>If services don't meet agreed specifications, we'll correct issues at no extra cost</ListItem>
                <ListItem>Report issues within 48 hours of delivery</ListItem>
                <ListItem>We'll have 5 business days to resolve the issue</ListItem>
                <ListItem>If not resolved, you may request a partial refund or cancellation</ListItem>
              </ul>
            </PolicySection>

            <PolicySection title="6. How to Request a Refund">
              <p style={pStyle}>
                Follow these steps to request a refund:
              </p>
              <h3 style={h3Style}>Refund Request Process</h3>
              <ul style={ulStyle}>
                <ListItem><span style={strongStyle}>Step 1:</span> Contact our support team at support@workdeskva.com</ListItem>
                <ListItem><span style={strongStyle}>Step 2:</span> Provide your order number and reason for refund</ListItem>
                <ListItem><span style={strongStyle}>Step 3:</span> Include any supporting documentation or evidence</ListItem>
                <ListItem><span style={strongStyle}>Step 4:</span> Our team will review and respond within 5-7 business days</ListItem>
                <ListItem><span style={strongStyle}>Step 5:</span> If approved, refund will be processed to original payment method</ListItem>
              </ul>

              <h3 style={h3Style}>Required Information</h3>
              <ul style={ulStyle}>
                <ListItem>Your account email address</ListItem>
                <ListItem>Order or invoice number</ListItem>
                <ListItem>Service start and end dates (if applicable)</ListItem>
                <ListItem>Detailed reason for refund request</ListItem>
                <ListItem>Any relevant screenshots or documentation</ListItem>
              </ul>
            </PolicySection>

            <PolicySection title="7. Special Cases & Exceptions">
              <h3 style={h3Style}>Team Member Illness or Emergency</h3>
              <p style={pStyle}>
                If a team member becomes unavailable due to illness or emergency:
              </p>
              <ul style={ulStyle}>
                <ListItem>We'll notify you immediately</ListItem>
                <ListItem>Offer a qualified replacement at no cost</ListItem>
                <ListItem>Prorated credit for service interruption (more than 48 hours)</ListItem>
              </ul>

              <h3 style={h3Style}>Business Closure or Force Majeure</h3>
              <p style={pStyle}>
                In case of unforeseen circumstances (natural disaster, government action, etc.):
              </p>
              <ul style={ulStyle}>
                <ListItem>We'll notify all affected clients immediately</ListItem>
                <ListItem>Full refund of unused service period</ListItem>
                <ListItem>Alternative service arrangements if possible</ListItem>
              </ul>

              <h3 style={h3Style}>Dispute Resolution</h3>
              <ul style={ulStyle}>
                <ListItem>Disputes will be addressed through discussion with our support team</ListItem>
                <ListItem>Escalation to management if not resolved within 10 days</ListItem>
                <ListItem>Mediation available for unresolved disputes</ListItem>
                <ListItem>Final decision made within 30 days</ListItem>
              </ul>
            </PolicySection>

            <PolicySection title="8. Payment Method Considerations">
              <h3 style={h3Style}>Credit Card Refunds</h3>
              <ul style={ulStyle}>
                <ListItem>Refund processed as a credit to your original card</ListItem>
                <ListItem>May appear as negative charge on your statement</ListItem>
                <ListItem>Typically visible within 1-2 billing cycles</ListItem>
                <ListItem>Contact your bank if you don't see the refund after 2 cycles</ListItem>
              </ul>

              <h3 style={h3Style}>Bank Transfer Refunds</h3>
              <ul style={ulStyle}>
                <ListItem>Refund returned to original account</ListItem>
                <ListItem>Processing time: 3-5 business days after approval</ListItem>
                <ListItem>Your bank may take additional 3-7 business days</ListItem>
                <ListItem>Provide correct account details to avoid delays</ListItem>
              </ul>

              <h3 style={h3Style}>Account Credit</h3>
              <ul style={ulStyle}>
                <ListItem>Alternative to cash refund</ListItem>
                <ListItem>Available for future services or upgrades</ListItem>
                <ListItem>Valid for 12 months from issue date</ListItem>
                <ListItem>Can be transferred with account ownership (request required)</ListItem>
              </ul>
            </PolicySection>

            <PolicySection title="9. Subscription Renewals">
              <p style={pStyle}>
                For recurring monthly subscriptions:
              </p>
              <h3 style={h3Style}>Automatic Renewal</h3>
              <ul style={ulStyle}>
                <ListItem>Services renew automatically unless cancelled</ListItem>
                <ListItem>Renewal reminder sent 7 days before next billing date</ListItem>
                <ListItem>You can cancel anytime through your account dashboard</ListItem>
              </ul>

              <h3 style={h3Style}>Cancelling Renewal</h3>
              <ul style={ulStyle}>
                <ListItem>Cancel at least 3 days before renewal date to avoid charge</ListItem>
                <ListItem>Service continues through end of current billing period</ListItem>
                <ListItem>Cancellation confirmation sent immediately</ListItem>
              </ul>
            </PolicySection>

            <LastPolicySection title="10. Contact & Support">
              <p style={pStyle}>
                Have questions about our refund policy? Reach out to us:
              </p>
              <div style={contactBoxStyle}>
                <p style={{...pStyle, margin: '0'}}>
                  <span style={strongStyle}>WorkdeskVA Support Team</span><br />
                  Email: <a href="mailto:support@workdeskva.com" style={linkStyle}>support@workdeskva.com</a><br />
                  Phone: <a href="tel:+1234567890" style={linkStyle}>+1 (234) 567-890</a><br />
                  Hours: Monday - Friday, 9:00 AM - 6:00 PM (EST)<br />
                  Response Time: Within 24 business hours
                </p>
              </div>

              <h3 style={h3Style}>Additional Policies</h3>
              <ul style={ulStyle}>
                <ListItem>Review our <a href="/privacy-policy" style={linkStyle}>Privacy Policy</a> for data protection</ListItem>
                <ListItem>Check our <a href="/support" style={linkStyle}>Support Center</a> for help and guidance</ListItem>
                <ListItem>View our Terms of Service for complete service agreement</ListItem>
              </ul>

              <p style={{...pStyle, marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #ddd'}}>
                <span style={strongStyle}>Note:</span> This refund policy is subject to change. We'll notify all users of any material changes. Your continued use of WorkdeskVA services after policy updates constitutes acceptance of new terms.
              </p>
            </LastPolicySection>
          </div>
        </div>
      </div>
    </>
  );
};

export default RefundPolicy;

